import { SET_FILES } from "@actions/project/files"
import { SET_FILE_REQUEST } from "@actions/file_request"
import { refreshFiles } from "@api/project/files"
import { removeTransfer, updateTransfer } from "@api/transfer/"
import { addGlobalMessage } from "@api/utils/"
import { fetchWithBackoff, fetchJsonWithBackoff, FetchError } from "@api/transfer/backoffRetry"
import Tracking from "@helpers/tracking"
import { GLOBALS } from "@src/config"
import { PUT_DEFAULTS } from "@constants"
import { UPDATE_UTILS } from "../../actions/utils"

export const getUploadUrl = (model) => {
  if (model.project_id && !model.file_request_id) {
    return `${GLOBALS.API_URL}/projects/${model.project_id}/uploads`
  }
  return `${GLOBALS.API_URL}/uploads`
}

const getUploadItemUrl = (transfer) => `${getUploadUrl(transfer.specifications)}/${transfer.upload_id}`

// Time-based throttle for progress pings. Error pings always fire immediately.
const pingTimestamps = new Map()
// Tracks pings whose backoff retry chain (up to ~2 minutes on sustained
// 429s) hasn't settled yet, so a slow chain can't overlap with a new one
// started once MIN_PROGRESS_PING_INTERVAL has merely elapsed.
const pingsInFlight = new Set()
const MIN_PROGRESS_PING_INTERVAL = 15000

export const clearUploadPingTimestamp = (uploadId) => {
  pingTimestamps.delete(uploadId)
  pingsInFlight.delete(uploadId)
}

export const updateUpload = (transfer, error = null) => (dispatch, getState) => {
  if (!error) {
    if (pingsInFlight.has(transfer.upload_id)) return

    const now = Date.now()
    const lastPing = pingTimestamps.get(transfer.upload_id)
    if (lastPing != null && now - lastPing < MIN_PROGRESS_PING_INTERVAL) return
    pingTimestamps.set(transfer.upload_id, now)
    pingsInFlight.add(transfer.upload_id)
  }

  if (typeof window.uploadingEvent !== "undefined") {
    window.dispatchEvent(window.uploadingEvent)
  }

  const url = getUploadItemUrl(transfer)
  const opts = {
    ...PUT_DEFAULTS,
    body: JSON.stringify(error ? { error } : {
      current_size: transfer.current_size,
      total_size: transfer.total_size,
      status: transfer.status,
      progress: transfer.progress
    })
  }

  fetchWithBackoff(() => fetch(url, opts))
    .then(res => {
      if (res.ok) {
        dispatch({ type: UPDATE_UTILS, key: 'last_api_activity', value: Date.now() })
      } else if (res.body && res.body.cancel) {
        res.body.cancel()
      }
    })
    .finally(() => {
      if (!error) pingsInFlight.delete(transfer.upload_id)
    })
}

export const completeUpload = transfer => (dispatch, getState) => {
  dispatch(updateTransfer("current_size", transfer.total_size, transfer.uuid))
  dispatch(updateTransfer("status", "Complete", transfer.uuid))

  const url = getUploadItemUrl(transfer)
  const OPTS = {
    ...PUT_DEFAULTS,
    body: JSON.stringify({
      total_size: transfer.total_size,
      status: "Complete"
    })
  }

  fetchJsonWithBackoff(() => fetch(url, OPTS))
    .then(response => {
      dispatch(addGlobalMessage(`Completed upload of ${transfer.name}!`, "success"))
      dispatch(removeTransfer(transfer.uuid))
      Tracking.record("Upload file", {
        "Upload method": transfer.method,
        "File type": transfer.type,
        "File name": transfer.name,
        "File size": transfer.total_size
      })

      const folderID = getState().project.files.items.folder_id || null
      const projectID = getState().project.files.items.project_id || null

      // Old method of returning data from the back-end:
      if (response.upload && response.result) {
        const { upload, result } = response

        if (result.project_id === projectID && result.folder_id === folderID) {
          dispatch({
            type: SET_FILES,
            ...result
          })
        }

        if (upload.file_request_id || upload.cinerequest_id) {
          dispatch({
            type: SET_FILE_REQUEST,
            result
          })
        }

        /**
         * A callback is sometimes needed for state management reasons
         * Note: Ideally, this should be avoided, and instead use server response for state updates
         */
        if (transfer.callback) transfer.callback(result)

        return
      }

      // New method of returning data from the back-end:
      if (response.data) {
        const { data } = response
        const uploadedFolderID = data.folder_id || null
        if (data.project_id === projectID && uploadedFolderID === folderID) {
          dispatch(refreshFiles(projectID, folderID))
        }

        if (transfer.callback) transfer.callback()
      }

    })
    .catch(err => {
      // The optimistic Complete status above was never confirmed - reflect
      // that instead of leaving the transfer stuck silently, and still run
      // the caller's callback so any UI state gated on it (e.g. a
      // submitting/loading flag) doesn't hang forever.
      dispatch(updateTransfer("status", "Error", transfer.uuid))
      dispatch(updateTransfer("errorMsg", `Failed to confirm upload of ${transfer.name}. Please retry.`, transfer.uuid))
      dispatch(addGlobalMessage(`Failed to complete upload of ${transfer.name}`, "error"))
      if (transfer.callback) transfer.callback()

      if (!(err instanceof FetchError)) {
        console.error(err)
      }
    })
}
